import React, { useState, useEffect, useRef } from 'react';
import { X, Save, Upload, Loader2, Info, Image as ImageIcon } from 'lucide-react';
import CloudinaryService from '../../services/cloudinary';

const RESOLUTION_HINT = '1080 × 400 px recommended (landscape, 16:6 ratio) — JPEG, PNG or WEBP, max 2 MB';

// All navigable screens from RootNavigator (MainStack + PanditStack), deduplicated
const APP_SCREENS = [
  // --- Main Stack (Yajman / user-facing) ---
  { name: 'Home',                   label: 'Home',                      stack: 'Main' },
  { name: 'Profile',                label: 'Profile',                   stack: 'Main' },
  { name: 'EditProfile',            label: 'Edit Profile',              stack: 'Main' },
  { name: 'MyBookings',             label: 'My Bookings',               stack: 'Main' },
  { name: 'BookingDetails',         label: 'Booking Details',           stack: 'Main' },
  { name: 'AllPandits',             label: 'All Pandits',               stack: 'Main' },
  { name: 'PanditDetails',          label: 'Pandit Details',            stack: 'Main' },
  { name: 'BookNow',                label: 'Book Now',                  stack: 'Main' },
  { name: 'TakeAppointment',        label: 'Take Appointment',          stack: 'Main' },
  { name: 'BookingConfirmation',    label: 'Booking Confirmation',      stack: 'Main' },
  { name: 'AppointmentConfirmation',label: 'Appointment Confirmation',  stack: 'Main' },
  { name: 'PujaList',               label: 'Puja List',                 stack: 'Main' },
  { name: 'PujaDetails',            label: 'Puja Details',              stack: 'Main' },
  { name: 'Horoscope',              label: 'Horoscope',                 stack: 'Main' },
  { name: 'HoroscopeDetails',       label: 'Horoscope Details',         stack: 'Main' },
  { name: 'MahuratDetails',         label: 'Mahurat Details',           stack: 'Main' },
  { name: 'LagnaMuhurat',           label: 'Lagna Muhurat',             stack: 'Main' },
  { name: 'Panchang',               label: 'Panchang',                  stack: 'Main' },
  { name: 'SanatanCalendar',        label: 'Sanatan Calendar',          stack: 'Main' },
  { name: 'Kundli',                 label: 'Kundli',                    stack: 'Main' },
  { name: 'TempleMap',              label: 'Temple Map',                stack: 'Main' },
  { name: 'Search',                 label: 'Search',                    stack: 'Main' },
  { name: 'SearchResults',          label: 'Search Results',            stack: 'Main' },
  { name: 'AddReview',              label: 'Add Review',                stack: 'Main' },
  { name: 'DeleteAccount',          label: 'Delete Account',            stack: 'Main' },
  { name: 'ChangePassword',         label: 'Change Password',           stack: 'Main' },
  { name: 'Books',                  label: 'Books (E-Library)',         stack: 'Main' },
  { name: 'BookDetails',            label: 'Book Details',              stack: 'Main' },
  { name: 'ReadBook',               label: 'Read Book',                 stack: 'Main' },
  { name: 'HelpSupport',            label: 'Help & Support',            stack: 'Main' },
  { name: 'AllFestivals',           label: 'All Festivals',             stack: 'Main' },
  { name: 'FestivalDetails',        label: 'Festival Details',          stack: 'Main' },
  { name: 'TirthYatraList',         label: 'Tirth Yatra List',          stack: 'Main' },
  { name: 'TirthYatraDetails',      label: 'Tirth Yatra Details',       stack: 'Main' },
  { name: 'PrivacyPolicy',          label: 'Privacy Policy',            stack: 'Main' },
  { name: 'TermsConditions',        label: 'Terms & Conditions',        stack: 'Main' },
  { name: 'ReturnPolicy',           label: 'Return Policy',             stack: 'Main' },
  { name: 'Chat',                   label: 'Chat',                      stack: 'Main' },
  { name: 'ChatConversation',       label: 'Chat Conversation',         stack: 'Main' },
  { name: 'PanditAbout',            label: 'Pandit About',              stack: 'Main' },
  { name: 'AllLiveEvents',          label: 'All Live Events',           stack: 'Main' },
  { name: 'EventDetails',           label: 'Live Event Details',        stack: 'Main' },
  { name: 'LiveStreamPayment',      label: 'Live Stream Payment',       stack: 'Main' },
  { name: 'ConsultationBooking',    label: 'Consultation Booking',      stack: 'Main' },
  { name: 'ConsultationPayment',    label: 'Consultation Payment',      stack: 'Main' },
  { name: 'MyConsultations',        label: 'My Consultations',          stack: 'Main' },
  { name: 'RescheduleBooking',      label: 'Reschedule Booking',        stack: 'Main' },
  // --- Pandit Stack ---
  { name: 'PanditHome',             label: 'Pandit Home',               stack: 'Pandit' },
  { name: 'PanditProfile',          label: 'Pandit Profile',            stack: 'Pandit' },
  { name: 'EditPanditProfile',      label: 'Edit Pandit Profile',       stack: 'Pandit' },
  { name: 'PanditBookings',         label: 'Pandit Bookings',           stack: 'Pandit' },
  { name: 'PanditBookingDetails',   label: 'Pandit Booking Details',    stack: 'Pandit' },
  { name: 'PanditSchedule',         label: 'Pandit Schedule',           stack: 'Pandit' },
  { name: 'PanditServices',         label: 'Pandit Services',           stack: 'Pandit' },
  { name: 'AddService',             label: 'Add Service',               stack: 'Pandit' },
  { name: 'PanditEarnings',         label: 'Pandit Earnings',           stack: 'Pandit' },
  { name: 'PanditTransactions',     label: 'Pandit Transactions',       stack: 'Pandit' },
  { name: 'PanditReviews',          label: 'Pandit Reviews',            stack: 'Pandit' },
  { name: 'CreateLiveStream',       label: 'Create Live Stream',        stack: 'Pandit' },
  { name: 'MyLiveStreams',          label: 'My Live Streams',           stack: 'Pandit' },
  { name: 'LiveStreamDetails',      label: 'Live Stream Details',       stack: 'Pandit' },
  { name: 'HostLiveStream',         label: 'Host Live Stream',          stack: 'Pandit' },
  { name: 'DocumentUpload',         label: 'Document Upload',           stack: 'Pandit' },
  { name: 'PanditConsultations',    label: 'Pandit Consultations',      stack: 'Pandit' },
  { name: 'PanditConsultationSetup',label: 'Pandit Consultation Setup', stack: 'Pandit' },
  { name: 'PanditConsultationBookings', label: 'Pandit Consultation Bookings', stack: 'Pandit' },
  { name: 'PanditSlotManagement',   label: 'Pandit Slot Management',    stack: 'Pandit' },
];

const AddEditBannerModal = ({ isOpen, onClose, onSave, banner = null, loading = false }) => {
  const [formData, setFormData] = useState({
    title: '',
    subtitle: '',
    imageUrl: '',
    linkType: 'none',
    linkValue: '',
    isActive: true,
    priority: 0,
  });

  const [errors, setErrors] = useState({});
  const [imageFile, setImageFile] = useState(null);
  const [previewImage, setPreviewImage] = useState('');
  const [imageUploading, setImageUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [showScreenSuggestions, setShowScreenSuggestions] = useState(false);
  const [screenQuery, setScreenQuery] = useState('');
  const fileInputRef = useRef(null);
  const screenInputRef = useRef(null);
  const suggestionsRef = useRef(null);
  const isEditMode = !!banner;

  useEffect(() => {
    if (banner) {
      setFormData({
        title: banner.title || '',
        subtitle: banner.subtitle || '',
        imageUrl: banner.imageUrl || '',
        linkType: banner.linkType || 'none',
        linkValue: banner.linkValue || '',
        isActive: banner.isActive ?? true,
        priority: banner.priority ?? 0,
      });
      setPreviewImage(banner.imageUrl || '');
      setScreenQuery(banner.linkValue || '');
    } else {
      setFormData({ title: '', subtitle: '', imageUrl: '', linkType: 'none', linkValue: '', isActive: true, priority: 0 });
      setPreviewImage('');
      setScreenQuery('');
    }
    setImageFile(null);
    setErrors({});
    setShowScreenSuggestions(false);
  }, [banner, isOpen]);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(e.target) &&
        screenInputRef.current &&
        !screenInputRef.current.contains(e.target)
      ) {
        setShowScreenSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const processFile = (file) => {
    if (!file) return;

    const validation = CloudinaryService.validateFile(file, {
      maxSize: 2 * 1024 * 1024, // 2 MB
      allowedTypes: ['.jpg', '.jpeg', '.png', '.webp'],
    });

    if (!validation.isValid) {
      setErrors((prev) => ({ ...prev, image: validation.errors.join(', ') }));
      return;
    }

    setImageFile(file);
    setErrors((prev) => ({ ...prev, image: '' }));

    const reader = new FileReader();
    reader.onload = (e) => setPreviewImage(e.target.result);
    reader.readAsDataURL(file);
  };

  const handleFileInput = (e) => processFile(e.target.files[0]);

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    processFile(e.dataTransfer.files[0]);
  };

  const uploadImage = async () => {
    if (!imageFile) return formData.imageUrl;

    setImageUploading(true);
    try {
      const result = await CloudinaryService.uploadFile(imageFile, {
        folder: 'vaidic/banners',
        tags: ['banner', 'home-screen', 'admin-upload'],
      });

      if (result.success) {
        return result.url;
      }
      throw new Error(result.error || 'Image upload failed');
    } catch (error) {
      setErrors((prev) => ({ ...prev, image: error.message }));
      return null;
    } finally {
      setImageUploading(false);
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!previewImage && !imageFile) newErrors.image = 'A banner image is required';
    if (formData.linkType !== 'none' && !formData.linkValue.trim()) {
      newErrors.linkValue = 'Link value is required when a link type is selected';
    }
    if (Number(formData.priority) < 0 || !Number.isInteger(Number(formData.priority))) {
      newErrors.priority = 'Priority must be a non-negative integer';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    // Upload image first if a new file was selected
    let finalImageUrl = formData.imageUrl;
    if (imageFile) {
      finalImageUrl = await uploadImage();
      if (!finalImageUrl) return; // upload failed — error already set
    }

    onSave({ ...formData, imageUrl: finalImageUrl, priority: parseInt(formData.priority, 10) });
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: '' }));
  };

  const isBusy = loading || imageUploading;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="p-6 border-b border-gray-200 sticky top-0 bg-white rounded-t-xl z-10">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900">
              {isEditMode ? 'Edit Banner' : 'Add New Banner'}
            </h2>
            <button onClick={onClose} disabled={isBusy} className="text-gray-400 hover:text-gray-600 disabled:opacity-50">
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Resolution hint */}
          <div className="flex items-start gap-2 bg-orange-50 border border-orange-200 rounded-lg p-3">
            <Info className="w-4 h-4 text-orange-500 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-orange-800">Recommended Image Resolution</p>
              <p className="text-xs text-orange-700 mt-0.5">{RESOLUTION_HINT}</p>
            </div>
          </div>

          {/* Image Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Banner Image <span className="text-red-500">*</span>
            </label>

            {/* Preview */}
            {previewImage && (
              <div className="relative mb-3 group">
                <img
                  src={previewImage}
                  alt="Banner preview"
                  className="w-full h-40 object-cover rounded-lg border border-gray-200"
                />
                <button
                  type="button"
                  onClick={() => {
                    setPreviewImage('');
                    setImageFile(null);
                    setFormData((prev) => ({ ...prev, imageUrl: '' }));
                    if (fileInputRef.current) fileInputRef.current.value = '';
                  }}
                  className="absolute top-2 right-2 bg-black bg-opacity-60 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                  title="Remove image"
                >
                  <X className="w-4 h-4" />
                </button>
                {imageFile && (
                  <span className="absolute bottom-2 left-2 bg-black bg-opacity-60 text-white text-xs px-2 py-0.5 rounded-full">
                    {imageFile.name}
                  </span>
                )}
              </div>
            )}

            {/* Drop zone */}
            <div
              onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              className={`w-full flex flex-col items-center justify-center px-4 py-8 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${
                isDragging
                  ? 'border-orange-400 bg-orange-50'
                  : 'border-gray-300 hover:border-orange-400 hover:bg-gray-50'
              } ${errors.image ? 'border-red-400' : ''}`}
            >
              {imageUploading ? (
                <Loader2 className="w-8 h-8 text-orange-500 animate-spin mb-2" />
              ) : (
                <Upload className="w-8 h-8 text-gray-400 mb-2" />
              )}
              <p className="text-sm text-gray-600 font-medium">
                {imageUploading
                  ? 'Uploading to Cloudinary…'
                  : previewImage
                  ? 'Click or drag to replace image'
                  : 'Click or drag & drop to upload banner'}
              </p>
              <p className="text-xs text-gray-400 mt-1">JPG, PNG, WEBP — max 2 MB</p>
              <input
                ref={fileInputRef}
                type="file"
                accept=".jpg,.jpeg,.png,.webp"
                onChange={handleFileInput}
                className="hidden"
                disabled={isBusy}
              />
            </div>
            {errors.image && <p className="mt-1 text-sm text-red-600">{errors.image}</p>}
          </div>

          {/* Title & Subtitle */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-sm font-medium text-gray-700">Title (optional)</label>
                <span className={`text-xs ${formData.title.length > 40 ? 'text-red-500 font-semibold' : 'text-gray-400'}`}>
                  {formData.title.length}/40
                </span>
              </div>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => handleChange('title', e.target.value.slice(0, 40))}
                placeholder="e.g. Navratri Special"
                maxLength={40}
                disabled={isBusy}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 disabled:opacity-50"
              />
              <p className="text-xs text-gray-400 mt-1">Shown as large heading over the banner</p>
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-sm font-medium text-gray-700">Subtitle (optional)</label>
                <span className={`text-xs ${formData.subtitle.length > 80 ? 'text-red-500 font-semibold' : 'text-gray-400'}`}>
                  {formData.subtitle.length}/80
                </span>
              </div>
              <input
                type="text"
                value={formData.subtitle}
                onChange={(e) => handleChange('subtitle', e.target.value.slice(0, 80))}
                placeholder="e.g. Book your puja now"
                maxLength={80}
                disabled={isBusy}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 disabled:opacity-50"
              />
              <p className="text-xs text-gray-400 mt-1">Shown as smaller text below the title</p>
            </div>
          </div>

          {/* Link Type & Value */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Link Type</label>
              <select
                value={formData.linkType}
                onChange={(e) => {
                  handleChange('linkType', e.target.value);
                  handleChange('linkValue', '');
                  setScreenQuery('');
                  setShowScreenSuggestions(false);
                }}
                disabled={isBusy}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 disabled:opacity-50"
              >
                <option value="none">No Link</option>
                <option value="url">External URL</option>
                <option value="screen">App Screen</option>
              </select>
            </div>
            {formData.linkType !== 'none' && (
              <div className="relative">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {formData.linkType === 'url' ? 'URL' : 'Screen Name'} <span className="text-red-500">*</span>
                </label>

                {formData.linkType === 'screen' ? (
                  <>
                    <input
                      ref={screenInputRef}
                      type="text"
                      value={screenQuery}
                      onChange={(e) => {
                        const val = e.target.value;
                        setScreenQuery(val);
                        handleChange('linkValue', val);
                        setShowScreenSuggestions(true);
                      }}
                      onFocus={() => setShowScreenSuggestions(true)}
                      placeholder="Type to search screens…"
                      disabled={isBusy}
                      autoComplete="off"
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 disabled:opacity-50 ${
                        errors.linkValue ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />

                    {showScreenSuggestions && (() => {
                      const q = screenQuery.toLowerCase();
                      const matches = APP_SCREENS.filter(
                        (s) =>
                          s.name.toLowerCase().includes(q) ||
                          s.label.toLowerCase().includes(q)
                      );
                      if (matches.length === 0) return null;
                      return (
                        <ul
                          ref={suggestionsRef}
                          className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-56 overflow-y-auto"
                        >
                          {matches.map((s) => (
                            <li
                              key={s.name}
                              onMouseDown={(e) => {
                                e.preventDefault();
                                setScreenQuery(s.name);
                                handleChange('linkValue', s.name);
                                setShowScreenSuggestions(false);
                              }}
                              className="flex items-center justify-between px-3 py-2 cursor-pointer hover:bg-orange-50 text-sm"
                            >
                              <span className="font-medium text-gray-900">{s.name}</span>
                              <span className="text-xs text-gray-400 ml-2">{s.label} · {s.stack}</span>
                            </li>
                          ))}
                        </ul>
                      );
                    })()}

                    <p className="text-xs text-gray-400 mt-1">
                      Exact screen name registered in the app navigator
                    </p>
                  </>
                ) : (
                  <input
                    type="text"
                    value={formData.linkValue}
                    onChange={(e) => handleChange('linkValue', e.target.value)}
                    placeholder="https://..."
                    disabled={isBusy}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 disabled:opacity-50 ${
                      errors.linkValue ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                )}

                {errors.linkValue && <p className="mt-1 text-sm text-red-600">{errors.linkValue}</p>}
              </div>
            )}
          </div>

          {/* Priority & Active */}
          <div className="grid grid-cols-2 gap-4 items-end">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Priority <span className="text-xs text-gray-500">(0 = shown first)</span>
              </label>
              <input
                type="number"
                min="0"
                value={formData.priority}
                onChange={(e) => handleChange('priority', e.target.value)}
                disabled={isBusy}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 disabled:opacity-50 ${
                  errors.priority ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {errors.priority && <p className="mt-1 text-sm text-red-600">{errors.priority}</p>}
            </div>
            <div className="pb-1">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.isActive}
                  onChange={(e) => handleChange('isActive', e.target.checked)}
                  disabled={isBusy}
                  className="rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                />
                <span className="text-sm font-medium text-gray-700">Active (visible on app)</span>
              </label>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              disabled={isBusy}
              className="px-6 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isBusy}
              className="inline-flex items-center px-6 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isBusy ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              {imageUploading
                ? 'Uploading Image…'
                : loading
                ? isEditMode ? 'Updating…' : 'Creating…'
                : isEditMode ? 'Update Banner' : 'Create Banner'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddEditBannerModal;
